import { AddEmployerFormGroup } from '../forms/add-employer-form-group';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { EmployerOverviewFormGroup } from '../forms/employer-overview-form-group';
import { isoDateStringValidator } from '../../../../validators/iso-date-string-validator';
import { EmployerStatusTypeEnum } from '../../../../models/employers/enum/employer-status-type.enum';
import { AddressFormGroup } from '../../../../forms/address-form-group';
import { EmployerMailingAddressFormGroup } from '../../../../forms/employer-mailing-address-form-group';
import { EmployerPrimaryContactFormGroup } from '../forms/employer-primary-contact-form-group';
import { CANADA_COUNTRY_CODE } from '../../../../consts/consts';
import { emailValidator } from '../../../../validators/email.validator';
import { phoneNumberWithCountryValidator } from '../../../../validators/phone-number-with-country-validator';
import { AddressFormFactory } from '../../../../form-factories/address-form-factory';
import { EmployerAdditionalContactFormGroup } from '../forms/employer-additional-contact-form-group';
import { atLeastOneSigningAuthorityValidator } from '../step-components/add-employer-contact-personnel-step/validators/at-least-one-signing-authority.validator';
import { EmployerPlanFormGroup } from '../forms/employer-plan-form-group';
import { EmployerPlanTypeEnum } from '../../../../models/employers/enum/employer-plan-type.enum';
import { DesignatedUnitFormGroup } from '../forms/designated-unit-form-group';
import { DesignatedUnitGeneralDetailsFormGroup } from '../forms/designated-unit-general-details-form-group';
import { DesignatedUnitContributionDetailsFormGroup } from '../forms/designated-unit-contribution-details-form-group';
import { contributionRatePercentageValidator } from '../../../../validators/contribution-rate-percentage.validator';
import { contributionRateCalculationTypeValidator } from '../../../../validators/contribution-rate-calculation-type.validator';
import { minimumPercentageRangeValidator } from '../../../../validators/minimum-percentage-range.validator';
import { dateRangeValidator } from '../../../../validators/date-range-validator';
import { endDateRequiredWhenBackdatedValidator } from '../../../../validators/end-date-required-when-backdated.validator';
import { maxFileSizeValidator } from '../../../../ui/form-components/file-uploader/file-upload-field/max-file-size-validator';
import { DesignatedUnitParticipationTypeIdEnum } from '../../../../models/employers/enum/designated-unit-participation-type-id.enum';
import { uniqueDesignatedUnitNameFromListValidator } from '../../../../validators/unique-designated-unit-name.validator';
import { uniqueClientUnitIdFromListValidator } from '../../../../validators/unique-client-unit-id-validator/unique-client-unit-id.validator';

export class AddEmployerFormFactory {
  public static createAddEmployerForm(): AddEmployerFormGroup {
    return new FormGroup(
      {
        employerOverview: this.createEmployerOverviewFormGroup(),
        headOfficeAddress: this.createHeadOfficeAddressFormGroup(),
        mailingAddress: this.createMailingAddressFormGroup(),
        primaryContact: this.createEmployerPrimaryContactFormGroup(),
        additionalContacts: new FormArray<EmployerAdditionalContactFormGroup>([]),
        plan: this.createEmployerPlanFormGroup(),
        logo: new FormControl<File[] | null>(null, { validators: [maxFileSizeValidator(3)] }),
        supportingDocuments: new FormControl<File[] | null>(null, {
          validators: [maxFileSizeValidator(3), Validators.required]
        })
      },
      { validators: [atLeastOneSigningAuthorityValidator] }
    );
  }

  private static createEmployerOverviewFormGroup(): EmployerOverviewFormGroup {
    return new FormGroup({
      employerName: new FormControl<string | null>(null, { validators: [Validators.required] }),
      employerShortName: new FormControl<string | null>(null, { validators: [Validators.required] }),
      employerStatus: new FormControl<number>(EmployerStatusTypeEnum.Active, {
        nonNullable: true,
        validators: [Validators.required]
      }),
      employerType: new FormControl<number | null>(null, { validators: [Validators.required] }),
      organizationCreationDate: new FormControl<string | null>(null, {
        validators: [Validators.required, isoDateStringValidator]
      }),
      entranceDate: new FormControl<string | null>(null, { validators: [Validators.required, isoDateStringValidator] }),
      governingJurisdiction: new FormControl<number | null>(null, { validators: [Validators.required] }),
      votingRegion: new FormControl<number | null>(null, { validators: [Validators.required] }),
      remittanceAccountNumber: new FormControl<string | null>(null, { validators: [Validators.required] })
    });
  }

  private static createHeadOfficeAddressFormGroup(): AddressFormGroup {
    return AddressFormFactory.createAddressForm();
  }

  private static createMailingAddressFormGroup(): EmployerMailingAddressFormGroup {
    return new FormGroup({
      attention: new FormControl<string | null>(null),
      sameAsHeadOffice: new FormControl<boolean>(false, { nonNullable: true }),
      address: AddressFormFactory.createAddressForm()
    });
  }

  public static createEmployerPrimaryContactFormGroup(): EmployerPrimaryContactFormGroup {
    return new FormGroup(
      {
        id: new FormControl<number | null>(null),
        hasSigningAuthority: new FormControl(false, { nonNullable: true }),
        firstName: new FormControl<string | null>(null, { validators: [Validators.required] }),
        lastName: new FormControl<string | null>(null, { validators: [Validators.required] }),
        jobTitle: new FormControl<string | null>(null, { validators: [Validators.required] }),
        address: AddressFormFactory.createAddressForm(),
        email: new FormControl<string | null>(null, {
          validators: [Validators.required, emailValidator]
        }),
        phoneNumber: new FormControl<string | null>(null, { validators: [Validators.required] }),
        phoneNumberCountryCode: new FormControl<string | null>(CANADA_COUNTRY_CODE, {
          validators: [Validators.required]
        })
      },
      {
        validators: [phoneNumberWithCountryValidator()]
      }
    );
  }

  public static createEmployerAdditionalContactFormGroup(): EmployerAdditionalContactFormGroup {
    return new FormGroup(
      {
        id: new FormControl<number | null>(null),
        hasSigningAuthority: new FormControl(false, { nonNullable: true }),
        firstName: new FormControl<string | null>(null, { validators: [Validators.required] }),
        lastName: new FormControl<string | null>(null, { validators: [Validators.required] }),
        jobTitle: new FormControl<string | null>(null, { validators: [Validators.required] }),
        contactTopic: new FormControl<string | null>(null, {
          validators: [Validators.required, Validators.maxLength(40)]
        }),
        address: AddressFormFactory.createAddressForm(),
        email: new FormControl<string | null>(null, {
          validators: [Validators.required, emailValidator]
        }),
        phoneNumber: new FormControl<string | null>(null, { validators: [Validators.required] }),
        phoneNumberCountryCode: new FormControl<string | null>(CANADA_COUNTRY_CODE, {
          validators: [Validators.required]
        })
      },
      {
        validators: [phoneNumberWithCountryValidator()]
      }
    );
  }

  private static createEmployerPlanFormGroup(): EmployerPlanFormGroup {
    const fg = new FormGroup({
      employerPlanTypeId: new FormControl<number>(EmployerPlanTypeEnum.DefinedContribution, {
        validators: [Validators.required],
        nonNullable: true
      }),
      planEstablishmentDate: new FormControl<string | null>(null, {
        validators: [Validators.required, isoDateStringValidator]
      }),
      designatedUnits: new FormArray<DesignatedUnitFormGroup>([])
    });

    fg.controls.employerPlanTypeId.disable();
    return fg;
  }

  public static createDesignatedUnitFormGroup(formArray: FormArray<DesignatedUnitFormGroup>): DesignatedUnitFormGroup {
    return new FormGroup({
      generalDetails: this.createDesignatedUnitGeneralDetailsFormGroup(formArray),
      contributionDetails: this.createDesignatedUnitContributionDetailsFormGroup()
    });
  }

  public static createDesignatedUnitGeneralDetailsFormGroup(
    formArray: FormArray<DesignatedUnitFormGroup>
  ): DesignatedUnitGeneralDetailsFormGroup {
    const unitNames = formArray.controls.map(
      controls => controls?.controls?.generalDetails?.controls?.unitName?.getRawValue() ?? ''
    );
    const clientUnitIds = formArray.controls.map(
      controls => controls?.controls?.generalDetails?.controls?.clientUnitId?.getRawValue() ?? ''
    );
    return new FormGroup(
      {
        unitName: new FormControl<string | null>(null, {
          validators: [Validators.required, uniqueDesignatedUnitNameFromListValidator(unitNames)]
        }),
        clientUnitId: new FormControl<string | null>(null, {
          validators: [Validators.required, uniqueClientUnitIdFromListValidator(clientUnitIds)]
        }),
        localId: new FormControl<number | null>(null),
        startDate: new FormControl<string | null>(null, {
          validators: [Validators.required, isoDateStringValidator]
        }),
        endDate: new FormControl<string | null>(null, { validators: [isoDateStringValidator] }),
        designatedUnitTypeId: new FormControl<number | null>(null, { validators: [Validators.required] }),
        remittanceFrequencyTypeId: new FormControl<number | null>(null, { validators: [Validators.required] })
      },
      {
        validators: [
          dateRangeValidator('startDate', 'endDate'),
          endDateRequiredWhenBackdatedValidator('startDate', 'endDate')
        ]
      }
    );
  }

  public static createDesignatedUnitContributionDetailsFormGroup(): DesignatedUnitContributionDetailsFormGroup {
    return new FormGroup({
      contributionRateBasisTypeId: new FormControl<number | null>(null, { validators: [Validators.required] }),
      matchedContributionRateTypeId: new FormControl<number | null>(null, {
        validators: [
          Validators.required,
          contributionRateCalculationTypeValidator('matchedContributionRateTypeId', 'unmatchedContributionRateTypeId')
        ]
      }),
      matchedContributionRatePercentage: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(9), Validators.min(1)]
      }),
      matchedContributionRateRangeFirstPercentage: new FormControl<number | null>(null, {
        validators: [
          Validators.required,
          contributionRatePercentageValidator(
            'matchedContributionRateRangeFirstPercentage',
            'unmatchedContributionRateRangeFirstPercentage'
          ),
          minimumPercentageRangeValidator(
            'matchedContributionRateRangeFirstPercentage',
            'matchedContributionRateRangeSecondPercentage'
          ),
          Validators.max(9),
          Validators.min(1)
        ]
      }),
      matchedContributionRateRangeSecondPercentage: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(9), Validators.min(1)]
      }),
      unmatchedContributionRateTypeId: new FormControl<number | null>(null, {
        validators: [
          Validators.required,
          contributionRateCalculationTypeValidator('matchedContributionRateTypeId', 'unmatchedContributionRateTypeId')
        ]
      }),
      unmatchedContributionRatePercentage: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(9), Validators.min(1)]
      }),
      unmatchedContributionRateRangeFirstPercentage: new FormControl<number | null>(null, {
        validators: [
          Validators.required,
          contributionRatePercentageValidator(
            'matchedContributionRateRangeFirstPercentage',
            'unmatchedContributionRateRangeFirstPercentage'
          ),
          minimumPercentageRangeValidator(
            'unmatchedContributionRateRangeFirstPercentage',
            'unmatchedContributionRateRangeSecondPercentage'
          ),
          Validators.max(9),
          Validators.min(1)
        ]
      }),
      unmatchedContributionRateRangeSecondPercentage: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(9), Validators.min(1)]
      }),
      fullTimeParticipationType: new FormControl<string>(
        { value: $localize`Mandatory`, disabled: true },
        { nonNullable: true }
      ),
      fullTimeWaitingPeriod: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(24)]
      }),
      lessThanFullTimeParticipationTypeId: new FormControl<number>(DesignatedUnitParticipationTypeIdEnum.Optional, {
        validators: [Validators.required],
        nonNullable: true
      }),
      lessThanFullTimeWaitingPeriod: new FormControl<number | null>(null, {
        validators: [Validators.required, Validators.max(24)]
      })
    });
  }
}
